"use client";

import { useMemo, useState } from "react";
import { usePathname } from "next/navigation";
import {
  Copy,
  Facebook,
  Linkedin,
  MessageCircle,
  Share2,
  X
} from "lucide-react";
import { trackEvent } from "@/utils/gtm";

export interface SocialShareWidgetProps {
  url: string;
  title?: string;
  description?: string;
}

export default function SocialShareWidget({
  url,
  title = "",
  description = "",
}: SocialShareWidgetProps) {
  const pathname = usePathname();
  const currentPage = pathname || "/";
  const [copied, setCopied] = useState(false);

  const encodedUrl = encodeURIComponent(url || "");
  const encodedTitle = encodeURIComponent(title || description || "");

  const platforms = useMemo(
    () => [
      {
        id: "LinkedIn",
        href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
        Icon: Linkedin,
        ariaLabel: "Share on LinkedIn (opens in a new tab)",
      },
      {
        id: "Twitter/X",
        href: `https://x.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
        Icon: X,
        ariaLabel: "Share on X / Twitter (opens in a new tab)",
      },
      {
        id: "Facebook",
        href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
        Icon: Facebook,
        ariaLabel: "Share on Facebook (opens in a new tab)",
      },
      {
        id: "WhatsApp",
        href: `https://wa.me/?text=${encodeURIComponent(`${title || description || ""} ${url || ""}`)}`,
        Icon: MessageCircle,
        ariaLabel: "Share via WhatsApp (opens in a new window)",
      },
    ],
    [encodedTitle, encodedUrl, description, title, url]
  );

  const handleTrack = (platform: string) => {
    trackEvent("SocialShare", "click", platform, currentPage);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url || "");
      handleTrack("Copy Link");
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section className="max-w-[1440px] mx-auto px-4 lg:px-8 py-10">
      <div className="bg-white rounded-2xl border border-gray-200 p-5 md:p-6">
        <div className="flex items-center gap-2 text-[#1A1A1A] font-bold text-sm mb-4 md:mb-5">
          <Share2 size={16} className="text-[#1B61FF]" />
          Share This
        </div>

        <div className="hidden md:flex items-center gap-3">
          {platforms.map(({ id, href, Icon }) => (
            <a
              key={id}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleTrack(id)}
              className="w-11 h-11 rounded-full border border-gray-200 text-[#666666] hover:text-[#1B61FF] hover:border-[#1B61FF] inline-flex items-center justify-center transition-colors"
              aria-label={`Share on ${id}`}
            >
              <Icon size={18} />
            </a>
          ))}

          <button
            type="button"
            onClick={handleCopyLink}
            className="w-11 h-11 rounded-full border border-gray-200 text-[#666666] hover:text-[#1B61FF] hover:border-[#1B61FF] inline-flex items-center justify-center transition-colors"
            aria-label="Copy link"
          >
            <Copy size={18} />
          </button>

          {copied ? <span className="text-sm text-green-600 font-semibold">Copied!</span> : null}
        </div>

        <div className="md:hidden grid grid-cols-1 gap-2">
          {platforms.map(({ id, href, Icon }) => (
            <a
              key={`mobile-${id}`}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleTrack(id)}
              className="w-full px-4 py-3 rounded-lg border border-gray-200 text-[#1A1A1A] font-semibold text-sm inline-flex items-center gap-2"
              aria-label={`Share on ${id}`}
            >
              <Icon size={16} className="text-[#1B61FF]" />
              {id}
            </a>
          ))}

          <button
            type="button"
            onClick={handleCopyLink}
            className="w-full px-4 py-3 rounded-lg border border-gray-200 text-[#1A1A1A] font-semibold text-sm inline-flex items-center gap-2"
            aria-label="Copy link"
          >
            <Copy size={16} className="text-[#1B61FF]" />
            {copied ? "Copied!" : "Copy Link"}
          </button>
        </div>
      </div>
    </section>
  );
}

